import { v4 as uuid } from 'uuid'
import { memo, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { alpha, Box, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import { ExtraSmallButton } from '../custom'
import MoreHorizontalIcon from '../../assets/icons/MoreHorizontalIcon'
import { makeStyles } from 'tss-react/mui'
import ExitIcon from '../../assets/icons/ExitIcon'
import { generatePath, useNavigate } from 'react-router-dom'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import Chart from '../../assets/icons/Chart'
import { TradeOrder } from '../../reducer/strategyBuilder.reducer'
import { STRATEGY_BUILDER_LOCATION_PENDING_KEY, type StrategyBuilderLocationState } from '../../util/strategyBuilderFromPositionsNavigation'
import ROUTES from '../../assets/routes'
import { exchangeToJSON, TimeBucket_TimeSeries } from '../../proto/generated/common'
import TuneOutlinedIcon from '../../assets/icons/TuneOutlinedIcon'
import { useSelector } from 'react-redux'
import { selectOrderBooks } from '../../selectors/security'
import { getSquareOffOrder } from '../../util/positions'
import { GroupedPosition } from '../../reducer/positions.reducer'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { fetchOptionChainV2 } from '../../util/options'
import { GetOptionChainRequestV2 } from '../../proto/generated/candle-data-depot-option-chain'
import { Security } from '../../proto/generated/security'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../proto/generated/order'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import PlusIcon from '../../assets/icons/PlusIcon'
import { selectOrdersHistory } from '../../selectors/orders'
import { resolveBatchIdForBasketFromOrders } from '../../util/conditionalEntry'

export type ActionMenuProps = {
    position: GroupedPosition
    className?: string
    slTargetLabel: string
    onSlTargetClick: () => void
    isChildLeg?: boolean
    /** Single-leg `default_…` basket: offer conditional exit (opens rule modal directly). */
    standaloneConditionalExitEnabled?: boolean
    standaloneConditionalExitLabel?: string
    onStandaloneConditionalExitClick?: (event: MouseEvent) => void | Promise<void>
    /** Multi-leg / strategy basket after position has fills: CX from positions only. */
    basketConditionalExitEnabled?: boolean
    basketConditionalExitLabel?: string
    onBasketConditionalExitClick?: (event: MouseEvent) => void | Promise<void>
}

const ActionMenu: React.FC<ActionMenuProps> = ({
    position,
    className,
    slTargetLabel,
    onSlTargetClick,
    isChildLeg,
    standaloneConditionalExitEnabled,
    standaloneConditionalExitLabel,
    onStandaloneConditionalExitClick,
    basketConditionalExitEnabled,
    basketConditionalExitLabel,
    onBasketConditionalExitClick,
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { classes, cx } = useStyles()
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
    const { security: positionSecurity } = position
    const securityKey = useMemo(() => getSecurityKey(positionSecurity.exchange, positionSecurity.code), [positionSecurity])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [underlyingSecurity, setUnderlyingSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => {
        if (!securityKey) {
            setSecurity(undefined)
            setUnderlyingSecurity(undefined)
            return
        }
        getSecurityAsync(securityKey).then((s) => {
            setSecurity(s)
            if (s?.underlyingSecurityCode) {
                getSecurityAsync(getSecurityKey(s.exchange, s.underlyingSecurityCode)).then(setUnderlyingSecurity)
            } else {
                setUnderlyingSecurity(undefined)
            }
        })
    }, [securityKey])
    const underlyingToken = security?.underlyingSecurityCode ? getSecurityKey(security.exchange, security.underlyingSecurityCode) : ''
    const orderBooks = useSelector(selectOrderBooks)
    const orderBooksRef = useRef(orderBooks)
    
    useEffect(() => {
        orderBooksRef.current = orderBooks
    }, [orderBooks])
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const orderHistory = useSelector(selectOrdersHistory)

    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setMenuAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setMenuAnchorEl(null)
    }, [])

    const redirectToStrategyBuilder = useCallback(
        (navigationState?: StrategyBuilderLocationState) => {
            if (underlyingSecurity) {
                navigate(
                    generatePath(ROUTES.strategyBuilder, {
                        exchange: exchangeToJSON(underlyingSecurity.exchange),
                        symbol: underlyingSecurity.code
                    }),
                    navigationState ? { state: navigationState } : undefined
                )
            }
        },
        [underlyingSecurity, navigate]
    )

    const onAnalyzeClick = useCallback(
        async (event: any) => {
            handleClose()
            event.stopPropagation()
            if (!position) return

            const expiries = new Set<number>()
            let resolvedUnderlying: Security = null
            for (const p of position.children ?? []) {
                const sec = await getSecurityAsync(getSecurityKey(p.security.exchange, p.security.code))
                resolvedUnderlying = sec?.underlyingSecurityCode ? await getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode)) : null
                expiries.add(sec?.expiry?.expiry)
            }

            if (!resolvedUnderlying) resolvedUnderlying = position?.originalPosition?.security
                ? await getSecurityAsync(getSecurityKey(position.originalPosition.security.exchange, position.originalPosition.security.code))
                : null

            if (!resolvedUnderlying) return

            const payload: GetOptionChainRequestV2 = {
                security: { code: resolvedUnderlying.code, exchange: resolvedUnderlying.exchange },
                expiries: Array.from([...expiries]),
                noOfStrikes: -1,
                requestedFields: [],
                timeBucket: {
                    timeSeries: TimeBucket_TimeSeries.DAY,
                    value: 1
                },
                sendFutures: true
            }
            await fetchOptionChainV2(payload, dispatch, optionsTradesData, 'both', false)
            const batchId = uuid()
            redirectToStrategyBuilder({
                [STRATEGY_BUILDER_LOCATION_PENDING_KEY]: {
                    kind: 'analyze',
                    batchId,
                    basketId: position.groupId,
                    strategyGroup: position.groupName
                }
            })
        },
        [position, redirectToStrategyBuilder, optionsTradesData, handleClose, dispatch]
    )

    const onSquareOffClick = useCallback(
        async (event: any) => {
            event.stopPropagation()
            handleClose()
            if (!position) return

            const isStandalone = position.children?.length === 1 && position.children[0].groupId?.startsWith('default')

            if (isStandalone) {
                const standalonePosition = position.children[0]
                const securityKey = getSecurityKey(standalonePosition.security?.exchange, standalonePosition.security?.code)
                const standaloneSec = await getSecurityAsync(securityKey)
                const orderBookPrice = standalonePosition.quantity > 0 ? orderBooksRef.current[securityKey]?.bids[0]?.price : orderBooksRef.current[securityKey]?.asks[0]?.price

                const transactionType = standalonePosition.quantity > 0 ? TransactionType.SELL : TransactionType.BUY
                const quantity = Math.abs(standalonePosition.quantity)

                let finalProduct = standalonePosition.originalPosition?.product ?? Product.INTRADAY

                dispatch(
                    setQuickOrderData({
                        openDialog: true,
                        mode: 'regular',
                        type: 'trade',
                        isSquareOffOrder: true,
                        maxQuantity: quantity,
                        order: {
                            transactionType,
                            security: standaloneSec,
                            brokerOrderType: Broker_OrderType.MARKET_ORDER,
                            orderType: OrderType.NORMAL,
                            product: finalProduct,
                            price: orderBookPrice,
                            quantity
                        }
                    })
                )
                return
            }
            const orders: TradeOrder[] = []
            const expiries = new Set<number>()

            let resolvedUnderlying: Security = null
            for (const p of position.children ?? []) {
                if (!p.quantity) continue
                const sec = await getSecurityAsync(getSecurityKey(p.security.exchange, p.security.code))
                resolvedUnderlying = sec?.underlyingSecurityCode ? await getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode)) : null
                expiries.add(sec?.expiry?.expiry)
            }

            if (!resolvedUnderlying) resolvedUnderlying = position?.originalPosition?.security
                ? await getSecurityAsync(getSecurityKey(position.originalPosition.security.exchange, position.originalPosition.security.code))
                : null

            if (!resolvedUnderlying) return

            const payload: GetOptionChainRequestV2 = {
                security: { code: resolvedUnderlying.code, exchange: resolvedUnderlying.exchange },
                expiries: Array.from([...expiries]),
                noOfStrikes: -1,
                requestedFields: [],
                timeBucket: {
                    timeSeries: TimeBucket_TimeSeries.DAY,
                    value: 1
                },
                sendFutures: true
            }
            await fetchOptionChainV2(payload, dispatch, optionsTradesData, 'both', false)

            if (!position.children?.length) {
                const posSecKey = getSecurityKey(position.security?.exchange, position.security?.code)
                const posSec = await getSecurityAsync(posSecKey)
                position.quantity !== 0 &&
                    orders.push(
                        getSquareOffOrder(
                            {
                                ...position,
                                security: posSec
                            },
                            position.quantity > 0 ? orderBooksRef.current[posSecKey]?.bids[0]?.price : orderBooksRef.current[posSecKey]?.asks[0]?.price
                        )
                    )
            } else {
                for (const pos of position.children) {
                    const posSecKey = getSecurityKey(pos.security?.exchange, pos.security?.code)
                    const posSec = await getSecurityAsync(posSecKey)
                    pos.quantity !== 0 &&
                        orders.push(
                            getSquareOffOrder(
                                {
                                    ...pos,
                                    security: posSec
                                },
                                pos.quantity > 0 ? orderBooksRef.current[posSecKey]?.bids[0]?.price : orderBooksRef.current[posSecKey]?.asks[0]?.price
                            )
                        )
                }
            }

            const batchId =
                resolveBatchIdForBasketFromOrders(orderHistory, position.groupId) ?? uuid()

            redirectToStrategyBuilder({
                [STRATEGY_BUILDER_LOCATION_PENDING_KEY]: {
                    kind: 'squareOff',
                    batchId,
                    basketId: position.groupId,
                    strategyGroup: position.groupName,
                    orders
                }
            })
        },
        [position, redirectToStrategyBuilder, optionsTradesData, handleClose, dispatch]
    )

    const onStrategyChartClick = useCallback(
        (e: MouseEvent) => {
            e.stopPropagation()
            handleClose()
            if (!position) return

            if (position.children?.length == 1) {
                window.open(origin + `${generatePath(ROUTES.symbol, { symbol: getSecurityKey(position.security.exchange, position.security.code) })}`, '_blank')
            } else {
                window.open(`/strategy/${position.groupId}`, '_blank')
            }
        },
        [position, handleClose]
    )

    if (!position || position.quantity === 0) return null

    return (
        <Box className={cx(className, classes.moreButton)} onClick={(e) => e.stopPropagation()}>
            <ExtraSmallButton size="small" onClick={handleClick} sx={{ p: 0, height: '100%', width: '36px', borderRadius: '0 !important' }}>
                <MoreHorizontalIcon fontSize="small" />
            </ExtraSmallButton>
            <Menu
                open={Boolean(menuAnchorEl)}
                onClose={handleClose}
                anchorEl={menuAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                disableAutoFocusItem
                disableEnforceFocus
                PaperProps={{
                    sx: {
                        ml: 1,
                        '& .MuiMenu-list': { paddingTop: 0, paddingBottom: 0 }
                    }
                }}
            >
                {slTargetLabel && !isChildLeg && (
                    <MenuItem
                        sx={{ px: '5px' }}
                        onClick={(e) => {
                            e.stopPropagation()
                            onSlTargetClick()
                            handleClose()
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: '30px !important' }}>
                            <PlusIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography fontSize={'12px'}>{slTargetLabel}</Typography>
                        </ListItemText>
                    </MenuItem>
                )}
                {standaloneConditionalExitEnabled && onStandaloneConditionalExitClick && !isChildLeg && (
                    <MenuItem
                        sx={{ px: '5px' }}
                        onClick={async (e) => {
                            e.stopPropagation()
                            await onStandaloneConditionalExitClick(e)
                            handleClose()
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: '30px !important' }}>
                            <PlusIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography fontSize={'12px'}>
                                {standaloneConditionalExitLabel ?? 'Conditional exit'}
                            </Typography>
                        </ListItemText>
                    </MenuItem>
                )}
                {basketConditionalExitEnabled && onBasketConditionalExitClick && !isChildLeg && (
                    <MenuItem
                        sx={{ px: '5px' }}
                        onClick={async (e) => {
                            e.stopPropagation()
                            await onBasketConditionalExitClick(e)
                            handleClose()
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: '30px !important' }}>
                            <PlusIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography fontSize={'12px'}>
                                {basketConditionalExitLabel ?? 'Add conditional exit'}
                            </Typography>
                        </ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={onSquareOffClick} sx={{ px: '5px' }}>
                    <ListItemIcon sx={{ minWidth: '30px !important' }}>
                        <ExitIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography fontSize={'12px'}>Exit</Typography>
                    </ListItemText>
                </MenuItem>
                {(!position.groupId.startsWith("default_") && position.children?.length >= 1) && (
                    <MenuItem onClick={onAnalyzeClick} sx={{ px: '5px' }}>
                        <ListItemIcon sx={{ minWidth: '30px !important' }}>
                            <TuneOutlinedIcon sx={{ fontSize: '20px' }} />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography fontSize={'12px'}>Adjust/Analyze</Typography>
                        </ListItemText>
                    </MenuItem>
                )}
                {!isChildLeg && (
                    <MenuItem onClick={onStrategyChartClick} sx={{ px: '5px' }}>
                        <ListItemIcon sx={{ minWidth: '30px !important' }}>
                            <Chart sx={{ height: '24px', width: '24px' }} strokeWidth={1} />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography fontSize={'12px'}>Chart</Typography>
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </Box>
    )
}

export default memo(ActionMenu)

const useStyles = makeStyles()((theme) => {
    return {
        moreButton: {
            position: 'absolute',
            right: '5px',
            top: '0',
            height: '42px',
            display: 'flex',
            visibility: 'hidden',
            background: alpha(theme.palette.background.paper, 0.9),
            alignItems: 'center',
            zIndex: 2,
            borderRadius: '4px'
        }
    }
})
